import type { useGT } from 'gt-react';
import type { ReevaluateAPInvoiceExceptionsResult } from '@erp/rpc';

type Translate = ReturnType<typeof useGT>;

export type ReevaluationSummary = {
  succeeded: number;
  failed: number;
  stillOpen: number;
  resolved: number;
};

export type ReevaluateToast = {
  title: string;
  message: string;
  color: 'success' | 'warning' | 'error';
  icon: 'check' | 'warning' | 'error';
};

/** Summary of a bulk re-evaluate response, for the toast. */
export function summarizeReevaluation(
  results: readonly ReevaluateAPInvoiceExceptionsResult[],
): ReevaluationSummary {
  return results.reduce(
    (acc, result) => ({
      succeeded: acc.succeeded + (result.success ? 1 : 0),
      failed: acc.failed + (result.success ? 0 : 1),
      stillOpen: acc.stillOpen + (result.openCount ?? 0),
      resolved: acc.resolved + (result.resolvedCount ?? 0),
    }),
    { succeeded: 0, failed: 0, stillOpen: 0, resolved: 0 },
  );
}

export function reevaluateSuccessMessage(
  gt: Translate,
  summary: ReevaluationSummary,
): string {
  const invoices =
    summary.succeeded === 1
      ? gt('{count} invoice checked', {
          context: 'toast fragment: how many invoices were re-evaluated',
          count: String(summary.succeeded),
        })
      : gt('{count} invoices checked', {
          context: 'toast fragment: how many invoices were re-evaluated',
          count: String(summary.succeeded),
        });
  const open =
    summary.stillOpen === 1
      ? gt('{count} exception still open', {
          context:
            'toast fragment: how many exceptions remain open after re-evaluate',
          count: String(summary.stillOpen),
        })
      : gt('{count} exceptions still open', {
          context:
            'toast fragment: how many exceptions remain open after re-evaluate',
          count: String(summary.stillOpen),
        });
  const resolved =
    summary.resolved === 1
      ? gt('{count} exception resolved', {
          context:
            'toast fragment: how many exceptions were resolved after re-evaluate',
          count: String(summary.resolved),
        })
      : gt('{count} exceptions resolved', {
          context:
            'toast fragment: how many exceptions were resolved after re-evaluate',
          count: String(summary.resolved),
        });
  return gt('{invoices} · {open} · {resolved}', {
    context: 'toast body after re-evaluating AP invoice exceptions',
    invoices,
    open,
    resolved,
  });
}

/** Toast copy after a bulk re-evaluate mutation succeeds at the RPC layer. */
export function reevaluateToast(
  gt: Translate,
  results: readonly ReevaluateAPInvoiceExceptionsResult[],
): ReevaluateToast {
  const summary = summarizeReevaluation(results);
  if (summary.failed === 0) {
    return {
      title: gt('Exceptions re-evaluated', {
        context: 'toast title after re-evaluating AP invoice exceptions',
      }),
      message: reevaluateSuccessMessage(gt, summary),
      color: 'success',
      icon: 'check',
    };
  }
  const mixed = summary.succeeded > 0;
  return {
    title: gt('Some invoices could not be re-evaluated', {
      context: 'toast title when part of a re-evaluate batch failed',
    }),
    message:
      results.find((result) => !result.success)?.error ??
      gt('{count} failed', {
        context: 'fallback toast body for failed re-evaluations',
        count: String(summary.failed),
      }),
    color: mixed ? 'warning' : 'error',
    icon: mixed ? 'warning' : 'error',
  };
}
